import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Square, Loader } from 'lucide-react';

interface VoiceRecorderProps {
  onRecordingComplete: (blob: Blob) => void;
  isProcessing?: boolean;
  disabled?: boolean;
  maxDuration?: number; // seconds, default 120
}

type RecorderState = 'idle' | 'recording' | 'stopped';

export default function VoiceRecorder({
  onRecordingComplete,
  isProcessing = false,
  disabled = false,
  maxDuration = 120,
}: VoiceRecorderProps) {
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [supported, setSupported] = useState(true);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  // Check browser support
  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setSupported(false);
    }
  }, []);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Pick best supported MIME type
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const duration = (Date.now() - startTimeRef.current) / 1000;
        // Clean up stream
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }

        if (duration < 1) {
          setError('Recording too short — speak for at least 1 second');
          setState('idle');
          return;
        }

        const blob = new Blob(chunksRef.current, { type: mimeType });
        setState('idle');
        setElapsed(0);
        onRecordingComplete(blob);
      };

      recorder.start(250); // collect data every 250ms
      startTimeRef.current = Date.now();
      setState('recording');
      setElapsed(0);

      // Timer for elapsed display
      timerRef.current = setInterval(() => {
        const secs = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setElapsed(secs);
        if (secs >= maxDuration) {
          stopRecording();
        }
      }, 250);
    } catch (err: any) {
      cleanup();
      setState('idle');
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Microphone access required — check browser permissions');
      } else {
        setError('Could not access microphone');
      }
    }
  }, [maxDuration, onRecordingComplete, stopRecording, cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  if (!supported) return null;

  const handleClick = () => {
    if (isProcessing || disabled) return;
    if (state === 'recording') {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className="inline-flex flex-col items-start">
      <button
        onClick={handleClick}
        disabled={isProcessing || disabled}
        className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg transition-colors disabled:opacity-50 ${
          state === 'recording'
            ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
            : isProcessing
              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
              : 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
        }`}
      >
        {isProcessing ? (
          <>
            <Loader size={14} className="animate-spin" />
            Processing...
          </>
        ) : state === 'recording' ? (
          <>
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
            </span>
            {formatTime(elapsed)}
            <Square size={12} className="ml-0.5" />
            Stop
          </>
        ) : (
          <>
            <Mic size={14} />
            Voice Input
          </>
        )}
      </button>

      {error && (
        <p className="text-xs text-red-400 mt-1.5 max-w-[200px]">{error}</p>
      )}
    </div>
  );
}
